import { useEffect, useState } from "react";
import type { BacktestTrade } from "@/api/types";
import type { BacktestClosedTrade } from "@/lib/backtest-stats";
import { formatMoney, formatQty, formatBalance } from "@/lib/backtest-stats";
import { useFormatDate } from "@/hooks/useFormatDate";
import { Pager } from "@/components/ui/Pager";
import { clampPage } from "@/lib/paging";

const TRADES_PAGE_SIZE = 10;

function csvEscape(value: string | number | boolean): string {
  const s = String(value ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadClosedTradesCsv(trades: BacktestClosedTrade[], filename = "backtest-trades.csv") {
  const headers = [
    "trade",
    "direction",
    "entry_date",
    "exit_date",
    "entry_price",
    "exit_price",
    "qty",
    "size_fiat",
    "net_pnl",
    "return_pct",
    "fee",
    "balance",
    "is_open",
    "comment",
  ];
  // Match on-screen order (newest first).
  const rows = [...trades].reverse().map((t, i, arr) => {
    const n = arr.length - i;
    return [
      n,
      t.direction,
      t.entry_date,
      t.exit_date,
      t.entry_price,
      t.exit_price,
      t.qty,
      t.qty * t.entry_price,
      t.pnl,
      t.pnl_percent,
      t.fee,
      t.balance,
      t.is_open,
      t.comment || "",
    ]
      .map(csvEscape)
      .join(",");
  });
  const csv = [headers.join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function TradeTable({ trades }: { trades: BacktestTrade[] }) {
  const { dateTime } = useFormatDate();
  if (!trades.length) {
    return <div className="py-6 text-center text-sm text-[var(--text-muted)]">No trades</div>;
  }
  return (
    <div className="overflow-auto">
      <table className="dtable">
        <thead>
          <tr>
            {["#", "Date", "Action", "Direction", "Price", "Size", "Fee", "Net PnL", "Cum PnL", "Comment"].map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {trades.map((t) => (
            <tr key={t.id} className="border-t border-[var(--divider)] ">
              <td className="px-3 py-2">{t.id}</td>
              <td className="px-3 py-2 text-[var(--text-secondary)]">{dateTime(t.date)}</td>
              <td className="px-3 py-2">{t.action}</td>
              <td className="px-3 py-2">{t.direction}</td>
              <td className="px-3 py-2">{t.price.toFixed(4)}</td>
              <td className="px-3 py-2 whitespace-nowrap">
                <div>{formatQty(t.crypto)}</div>
                <div className="text-xs text-[var(--text-muted)]">{formatMoney(t.fiat)}</div>
              </td>
              <td className="px-3 py-2">{t.fee.toFixed(4)}</td>
              <td className="px-3 py-2" style={{ color: t.pnl >= 0 ? "var(--trading-profit)" : "var(--trading-loss)" }}>
                {formatMoney(t.pnl)}
              </td>
              <td className="px-3 py-2" style={{ color: t.cum_pnl >= 0 ? "var(--trading-profit)" : "var(--trading-loss)" }}>
                {formatMoney(t.cum_pnl)}
              </td>
              <td className="px-3 py-2 text-[var(--text-muted)]">{t.comment}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ClosedTradeTable({
  trades,
  exportFilename,
  title,
}: {
  trades: BacktestClosedTrade[];
  /** Optional download filename (defaults to backtest-trades.csv). */
  exportFilename?: string;
  /** When set, renders a shared title + Export header row (for split Live Trades view). */
  title?: string;
}) {
  const { dateTime } = useFormatDate();
  const [page, setPage] = useState(0);
  useEffect(() => {
    setPage(0);
  }, [trades]);
  const exportBtn = (
    <button
      type="button"
      className="btn btn-ghost btn-sm shrink-0"
      onClick={() => downloadClosedTradesCsv(trades, exportFilename)}
      disabled={trades.length === 0}
    >
      Export CSV
    </button>
  );

  if (!trades.length) {
    return (
      <div>
        {title && (
          <div className="mb-3 flex h-8 items-center justify-between gap-2">
            <h3 className="m-0 text-sm font-semibold">{title}</h3>
            {exportBtn}
          </div>
        )}
        <div className="py-6 text-center text-sm text-[var(--text-muted)]">No closed trades</div>
      </div>
    );
  }

  const rows = [...trades].reverse();
  const hasOpen = rows.some((t) => t.is_open);
  const safePage = clampPage(page, TRADES_PAGE_SIZE, rows.length);
  const pageStart = safePage * TRADES_PAGE_SIZE;
  const visible = rows.slice(pageStart, pageStart + TRADES_PAGE_SIZE);

  return (
    <div>
      {title ? (
        <div className="mb-3 flex h-8 items-center justify-between gap-2">
          <h3 className="m-0 text-sm font-semibold">{title}</h3>
          {exportBtn}
        </div>
      ) : (
        <div className="mb-3 flex h-8 items-center justify-end">{exportBtn}</div>
      )}
      <div className="overflow-auto">
        <table className="dtable">
          <thead>
            <tr>
              {[
                "Trade",
                "Type",
                "Date and time",
                "Price",
                "Size",
                "Net PnL",
                "Return",
                "Balance",
              ].map((h) => (
                <th key={h}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          {visible.map((t, i) => {
            const n = rows.length - (pageStart + i);
            const isLong = t.direction === "long";
            const dirColor = isLong ? "var(--trading-profit)" : "var(--trading-loss)";
            const pnlColor = t.pnl >= 0 ? "var(--trading-profit)" : "var(--trading-loss)";
            const exitLabel = t.is_open ? "Open†" : "Exit";
            const fiatSize = t.qty * t.entry_price;

            return (
              <tbody key={`${t.exit_date}-${i}`}>
                <tr>
                  <td className="px-3 py-2 align-middle whitespace-nowrap" rowSpan={2}>
                    <span className="text-[var(--text-primary)]">{n}</span>{" "}
                    <span className="font-medium capitalize" style={{ color: dirColor }}>
                      {t.direction}
                    </span>
                  </td>
                  <td className="px-3 pt-2 pb-1 text-[var(--text-secondary)]">{exitLabel}</td>
                  <td className="num px-3 pt-2 pb-1 text-[var(--text-muted)] whitespace-nowrap">
                    {dateTime(t.exit_date)}
                  </td>
                  <td
                    className={`num px-3 pt-2 pb-1 whitespace-nowrap ${t.is_open ? "italic" : ""}`}
                    style={t.is_open ? { color: "#d97706" } : undefined}
                    title={t.is_open ? "Mark price (last candle close) — not an actual exit fill" : undefined}
                  >
                    {t.exit_price.toFixed(4)}
                  </td>
                  <td className="num px-3 pt-2 pb-1 whitespace-nowrap align-middle" rowSpan={2}>
                    <div>{formatQty(t.qty)}</div>
                    <div className="text-xs text-[var(--text-muted)]">{formatMoney(fiatSize)}</div>
                  </td>
                  <td
                    className="num px-3 pt-2 pb-1 whitespace-nowrap align-middle font-medium"
                    rowSpan={2}
                    style={{ color: pnlColor }}
                  >
                    {formatMoney(t.pnl)}
                  </td>
                  <td
                    className="num px-3 pt-2 pb-1 whitespace-nowrap align-middle"
                    rowSpan={2}
                    style={{ color: pnlColor }}
                  >
                    {t.pnl_percent.toFixed(2)}%
                  </td>
                  <td className="num px-3 pt-2 pb-1 whitespace-nowrap align-middle" rowSpan={2}>
                    {formatBalance(t.balance)}
                  </td>
                </tr>
                <tr>
                  <td className="px-3 pt-1 pb-2 text-[var(--text-secondary)]">Entry</td>
                  <td className="num px-3 pt-1 pb-2 text-[var(--text-muted)] whitespace-nowrap">
                    {dateTime(t.entry_date)}
                  </td>
                  <td className="num px-3 pt-1 pb-2 whitespace-nowrap">{t.entry_price.toFixed(4)}</td>
                </tr>
              </tbody>
            );
          })}
        </table>
        {hasOpen && (
          <p className="mt-2 text-xs italic" style={{ color: "#d97706" }}>
            † Open position — price is the last candle close (mark), not an actual exit fill.
          </p>
        )}
        <Pager page={safePage} pageSize={TRADES_PAGE_SIZE} total={rows.length} onPage={setPage} />
      </div>
    </div>
  );
}
